/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IAny, ICoreContext, IGeneric, IGridSettings, IImageSettings, RichText } from '@msdyn365-commerce/core';
import { format } from '@msdyn365-commerce/retail-proxy';
import { DismissibleNotification, IClosableNotification, ITitledNotification,
    NotificationEvents, withNotificationComponent } from '@msdyn365-commerce-modules/notifications-core';
import { Event } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';

import { getTotalItems, IAddToCartInput, IItemsAddedToCartResources } from '../items-added-to-cart';
import { MultiItemsSuccessfullyAddedToCartNotificationComponent } from './multi-items-successfully-added-to-cart-notification-component';

/**
 * Notification which will be shown after the user adds multi items to the cart.
 */
@withNotificationComponent(MultiItemsSuccessfullyAddedToCartNotificationComponent)
export class MultiItemsSuccessfullyAddedToCartNotification extends DismissibleNotification implements ITitledNotification, IClosableNotification {
    public readonly context: ICoreContext<IGeneric<IAny>>;

    public readonly resources: IItemsAddedToCartResources;

    public readonly imageSettings?: IImageSettings;

    public readonly gridSettings?: IGridSettings;

    public readonly products: IAddToCartInput[];

    public readonly navigationUrl?: string;

    public readonly telemetryContent: ITelemetryContent;

    public readonly closeAriaLabel: string;

    public readonly title: RichText;

    public readonly descriptionLines: RichText[];

    public readonly componentId: string;

    public readonly typeName: string;

    public isClosed: boolean;

    public constructor(
        context: ICoreContext<IGeneric<IAny>>,
        resources: IItemsAddedToCartResources,
        imageSettings: IImageSettings | undefined,
        gridSettings: IGridSettings | undefined,
        products: IAddToCartInput[],
        navigationUrl: string | undefined,
        telemetryContent: ITelemetryContent,
        componentId: string,
        typeName: string
    ) {
        super();

        const itemsString = format(resources.itemsAddedToCartFormatText, getTotalItems(products).toString());
        const linesString = format(resources.linesAddedToCartFormatText, products.length.toString());

        this.title = `<b>${linesString}</b><span>${resources.itemsAddedToCartHeaderText}</span>`;
        this.descriptionLines = [];
        this.descriptionLines.push(`<b>${itemsString}</b><span>${resources.itemsAddedToCartHeaderText}</span>`);

        this.context = context;
        this.resources = resources;
        this.imageSettings = imageSettings;
        this.gridSettings = gridSettings;
        this.products = products;
        this.navigationUrl = navigationUrl;
        this.telemetryContent = telemetryContent;
        this.componentId = componentId;
        this.typeName = typeName;

        this.isClosed = false;
        this.closeAriaLabel = resources.closeButtonLabel;

        this.events.setValue(NotificationEvents.Close, new Event());
    }

    public close(): void {
        this.isClosed = true;
        this.events.getValue(NotificationEvents.Close)?.trigger();
    }
}
